/**
 * Configuration for Chatty component
 */

// Support both AWS (production) and local development
const USE_LOCAL_DEV = process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_LOCAL_LAMBDAS === 'true';

// Environment variables with fallbacks
// IMPORTANT: For production, the base API URL is https://api.limkc.com
// All Lambda endpoints follow pattern: https://api.limkc.com/upload/{endpoint}
// Examples:
//   - AI Chat: https://api.limkc.com/upload/ai
//   - Realtime Token: https://api.limkc.com/upload/realtime/token
//   - Future endpoints: https://api.limkc.com/upload/{anything}
export const API_GATEWAY_URL = USE_LOCAL_DEV 
  ? 'http://localhost:3001/ai-v2'
  : (process.env.REACT_APP_API_UPLOAD_GATEWAY_URL || 'https://api.limkc.com/upload/ai');
// Local LLM endpoints with fallback chain: localhost:11453 → LAN:11435 → cloud
export const KAIDEX_AI_API_PRIMARY = 'http://localhost:11453/v1/chat/completions'; // This Mac LLM server
export const KAIDEX_AI_API_SECONDARY = 'http://192.168.20.11:11435/v1/chat/completions'; // Linux (.11)
export const KAIDEX_AI_API = KAIDEX_AI_API_PRIMARY; // Default to primary

// Master switch: stream all OpenAI models (gpt-*, o1-*, o3-*, o4-*).
// ai_stream Lambda is built; this is now a KILL SWITCH only.
// Actual routing is gated by admin membership + per-browser StreamModeToggle (see chatApi.js _streamEligible).
// Public users never reach the streaming path regardless of this flag.
// Flip to false to force EVERYONE (including admin) back to ai_manager_v2 in an emergency.
export const OPENAI_STREAM = true;

// AI Model Configuration
export const ALL_AI_MODELS = {
  "kaidex-local": { supportsImages: true, endpoint: KAIDEX_AI_API, stream: false },
  // Local MLX model for development
  "default_model": { supportsImages: false, endpoint: "http://localhost:11453/v1/chat/completions", stream: false },
  // Local LoRA model for development
  "deepseek-r1:lora-default": { supportsImages: false, endpoint: "http://localhost:19868/api/chat", stream: true },
  // Standard Chat Models
  "gpt-4.1-mini": { supportsImages: true, endpoint: API_GATEWAY_URL, stream: OPENAI_STREAM },
  "gpt-5.1": { supportsImages: true, endpoint: API_GATEWAY_URL, stream: OPENAI_STREAM },
  "gpt-5.2": { supportsImages: true, endpoint: API_GATEWAY_URL, stream: OPENAI_STREAM },
  "gpt-5.3-codex": { supportsImages: true, endpoint: API_GATEWAY_URL, stream: OPENAI_STREAM },
  "gpt-5.4-mini": { supportsImages: true, endpoint: API_GATEWAY_URL, stream: OPENAI_STREAM },
  "gpt-5.4": { supportsImages: true, endpoint: API_GATEWAY_URL, stream: OPENAI_STREAM },
  "gpt-4.1": { supportsImages: false, endpoint: API_GATEWAY_URL, stream: OPENAI_STREAM },
  "o4-mini": { supportsImages: false, endpoint: API_GATEWAY_URL, stream: OPENAI_STREAM },
  // Reasoning Models (no tools, deep thinking)
  "gpt-5-codex": { supportsImages: false, endpoint: API_GATEWAY_URL, stream: OPENAI_STREAM },
  "o1-preview": { supportsImages: false, endpoint: API_GATEWAY_URL, stream: OPENAI_STREAM },
  "o1-mini": { supportsImages: false, endpoint: API_GATEWAY_URL, stream: OPENAI_STREAM },
  "o3-mini": { supportsImages: false, endpoint: API_GATEWAY_URL, stream: OPENAI_STREAM },
  // Grok Models
  "grok-2-1212": { supportsImages: false, endpoint: API_GATEWAY_URL, stream: false },
  "grok-2-vision-1212": { supportsImages: true, endpoint: API_GATEWAY_URL, stream: false },
  // Additional development-only models
  "claude-3-7-sonnet-20250219": { supportsImages: true, endpoint: API_GATEWAY_URL, stream: true },
  "gemini-2.5-pro-exp-03-25": { supportsImages: true, endpoint: API_GATEWAY_URL, stream: true }
};

export const PRODUCTION_AI_MODELS = ["gpt-4.1-mini", "grok-2-vision-1212"];

export const AVAILABLE_AI_MODELS = process.env.NODE_ENV === "development" 
  ? ALL_AI_MODELS 
  : Object.fromEntries(
      Object.entries(ALL_AI_MODELS).filter(([modelName]) => PRODUCTION_AI_MODELS.includes(modelName))
    );

export const DEFAULT_AI_MODEL = process.env.NODE_ENV === "development"
  ? "kaidex-local"
  : "gpt-5.4-mini";

// Feature flags
export const CLOUD_CHAT_STORAGE = false;
export const IS_POPUP_ENABLED = true;
export const ENABLE_MIMICK = true;
export const REAL_STREAMING = true;
export const DEBUG_STYLES = false;
export const VOICE_CHAT_ENABLED = true;
export const ELEVENLABS_S2S_ENABLED = true; // ElevenLabs Speech-to-Speech (admin/owner only)

// Model display names for UI
export const MODEL_DISPLAY_NAMES = {
  "kaidex-local": "Kaidex Local",
  "default_model": "Qwen 3 MLX",
  "gpt-4.1-mini": "GPT-4.1 Mini",
  "gpt-5.1": "GPT-5.1",
  "gpt-5.2": "GPT-5.2",
  "gpt-4.1": "GPT-4.1",
  "o4-mini": "O4 Mini",
  "gpt-5.3-codex": "GPT-5.3 Codex",
  "gpt-5.4-mini": "GPT-5.4 Mini",
  "gpt-5.4": "GPT-5.4",
  "gpt-5-codex": "GPT-5 Codex",
  "o1-preview": "O1 Preview",
  "o1-mini": "O1 Mini",
  "o3-mini": "O3 Mini",
  "grok-2-1212": "Grok-2-1212",
  "grok-2-vision-1212": "Grok-2-Vision-1212",
  "deepseek-r1:lora-default": "DeepSeek R1 LoRA",
  "claude-3-7-sonnet-20250219": "Claude 3.7 Sonnet",
  "gemini-2.5-pro-exp-03-25": "Gemini 2.5 Pro"
};

// Memory manager configuration
export const MEMORY_CONFIG = {
  summaryThreshold: 10,
  importantExchangesCount: 3,
  recentExchangesCount: 3,
  relevantExchangesCount: 2,
  maxTokens: 4000,
  useAISummarization: false
};

// Personality configuration
export const PERSONALITY_CONFIG = {
  friendliness: 0.9,
  formality: 0.3,
  creativity: 0.8,
  humor: 0.7,
  empathy: 0.9,
  role: "companion",
  specialInstructions: [
    "When the user mentions code or programming, provide examples to illustrate concepts.",
    "Always remember that the user prefers practical, actionable advice.",
    "If the user seems frustrated, acknowledge their feelings before providing solutions."
  ]
};

// Voice chat configuration
export const VOICE_CONFIG = {
  // OpenAI Realtime models available. Default = `gpt-realtime` (GA, released
  // Aug 2025). Legacy preview tiers retained for opt-in only.
  defaultModel: "gpt-realtime",
  models: {
    "gpt-realtime": {
      name: "GPT Realtime (GA)",
      quality: "high",
      description: "GA model — function calling in voice, improved transcripts"
    },
    "gpt-4o-realtime-preview": {
      name: "GPT-4o Realtime (preview, legacy)",
      cost: "$0.06-0.12/minute",
      quality: "high",
      description: "Legacy preview — kept for backward compatibility"
    },
    "gpt-4o-mini-realtime-preview": {
      name: "GPT-4o Mini Realtime (preview)",
      cost: "$0.03-0.06/minute",
      quality: "good",
      description: "Cheaper preview tier"
    }
  },
  
  // Default voice settings (Oct 2024 voices)
  defaultVoice: "alloy",
  availableVoices: {
    "alloy": "Neutral and balanced",
    "ash": "Clear and precise",
    "ballad": "Melodic and smooth", 
    "coral": "Warm and friendly",
    "echo": "Resonant and deep (male-like)",
    "sage": "Calm and thoughtful (mature)",
    "shimmer": "Bright and energetic",
    "verse": "Versatile and expressive"
  },
  
  // Audio settings
  audioConfig: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    sampleRate: 24000
  },
  
  // Voice Activity Detection
  vadConfig: {
    threshold: 0.5,
    prefixPaddingMs: 300,
    silenceDurationMs: 500
  },
  
  // Connection settings
  maxRetries: 3,
  connectionTimeout: 10000, // 10 seconds
  tokenExpiry: 60000 // 60 seconds
};

// ElevenLabs Conversational AI Configuration (WebSocket realtime voice chat)
export const ELEVENLABS_CONFIG = {
  // Conversational AI Agent
  agentId: 'agent_7201kekyj9qzej3r9xmx20byfz61',

  // WebSocket endpoint
  wsUrl: 'wss://api.elevenlabs.io/v1/convai/conversation',

  // Signed URL endpoint (backend Lambda - protects API key)
  // Uses /realtime/token which handles auth internally (same as OpenAI voice)
  signedUrlEndpoint: 'https://api.limkc.com/upload/realtime/token', // action: 'elevenlabs_signed_url'

  // Audio settings (ElevenLabs requires 16kHz 16-bit PCM)
  audioConfig: {
    sampleRate: 16000,
    channels: 1,
    format: 'pcm_16'
  }
};